import { useState, useEffect, FormEvent, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";
import { useIntl } from "../contexts/IntlContext";
import { UseFormHandlerOptions } from "../types/hooks";
import { useUnsavedChangesBlocker } from "./useUnsavedChangesBlocker";

export function useFormHandler<T extends Record<string, any>>({
  baseEndpoint,
  redirectPath,
  defaultData,
  successMessageId,
  errorMessageId,
  validateFields,
  transformData,
  loadData
}: UseFormHandlerOptions<T>) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  
  const [formData, setFormData] = useState<T>(defaultData);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const loadDataRef = useRef(loadData);
  const initialDataRef = useRef<T>(defaultData);

  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const { isBlocked: unsavedChangesBlocked, proceed: confirmDiscardChanges, cancel: cancelDiscardChanges } =
    useUnsavedChangesBlocker(hasChanges);

  useEffect(() => {
    if (isEdit && id && loadDataRef.current) {
      setLoading(true);
      loadDataRef.current(id)
        .then(data => {
          setFormData(data);
          initialDataRef.current = data;
          setHasChanges(false);
        })
        .catch((error: any) => {
          const message = error?.response?.data?.message || intl.formatMessage({ id: 'genericForm.loadError' });
          toast.error(message);
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, intl, reloadTrigger]);

  const updateField = useCallback((field: keyof T, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setFieldErrors(prev => {
      if (!prev[field as string]) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  }, []);

  const handleSubmit = async (event: FormEvent, continueEditing = false) => {
    event.preventDefault();

    const validation = validateFields(formData);
    if (!validation.valid) {
      toast.warn(validation.message || intl.formatMessage({ id: 'genericForm.fillAllRequiredFields' }));
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const dataToSend = transformData ? transformData(formData) : formData;
      const method = isEdit ? 'put' : 'post';
      const endpoint = isEdit ? `${baseEndpoint}/${id}` : baseEndpoint;
      
      const response = await api[method](endpoint, dataToSend);

      toast.success(intl.formatMessage({ id: successMessageId }));
      setHasChanges(false);

      if (continueEditing) {
        if (isEdit) {
          setReloadTrigger(prev => prev + 1);
        } else {
          navigate(location.pathname.replace(/[^/]*$/, response.data.id));
        }
      } else {
        navigate(redirectPath);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || intl.formatMessage({ id: errorMessageId });
      toast.error(message);

      const fields = error?.response?.data?.fields as { field: string; message: string }[] | undefined;
      if (Array.isArray(fields) && fields.length > 0) {
        setFieldErrors(
          fields.reduce<Record<string, string>>((acc, f) => {
            acc[f.field] = f.message;
            return acc;
          }, {}),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    updateField,
    handleSubmit,
    loading,
    isEdit,
    hasChanges,
    unsavedChangesBlocked,
    confirmDiscardChanges,
    cancelDiscardChanges,
    fieldErrors
  };
}